/**
 * One ontology evaluation draft, readable or editable.
 *
 * The model drafts and a human signs off, so a moderator may revise the prose
 * before publishing. Two things are deliberately NOT editable:
 *
 *  - evidence references, which are the checkable link between a claim and the
 *    ledger and are re-validated server-side at publish time. Hand-editing them
 *    would turn a citation into an assertion.
 *  - anything at all once published — publication is the point of no return.
 *
 * When a draft has been revised, the model's original wording stays available
 * behind a disclosure. A published evaluation records the model that produced it
 * and is shown to people as fact, so it has to remain possible to see which
 * words were generated and which were written by a moderator.
 */
import { formatDateTime } from '../../utils/format-date';
import { useState } from 'react';
import { FormField, Textarea } from '../../components/form-field';
import { Button } from '../../components/button';
import { useTranslation } from 'react-i18next';
import type { OntologyEvaluation } from '../../api/moderator/ontology/types';
import {
  usePublishOntologyEvaluation,
  useUpdateOntologyEvaluation,
} from '../../api/moderator/ontology/hooks';
import { confirmDialog } from '../../components/confirm-dialog/confirm-dialog-store';
import evalStyles from './evaluation.module.css';
import { ontologyEnumLabel } from './ontology-words';

/**
 * Draft/published tone, keyed rather than built from the status.
 *
 * Partial on purpose: SUPERSEDED has no rule and never had one — the old
 * `is-${status.toLowerCase()}` produced `is-superseded`, which matched nothing.
 * Writing the gap down is the difference between a decision and an oversight.
 */
const STATUS_TONE: Partial<Record<OntologyEvaluation['status'], string>> = {
  PUBLISHED: evalStyles.isPublished,
  DRAFT: evalStyles.isDraft,
};

interface EvaluationDraftCardProps {
  evaluation: OntologyEvaluation;
  matchId: string;
  onSelectAction: (actionId: string) => void;
}

export function EvaluationDraftCard({
  evaluation,
  matchId,
  onSelectAction,
}: EvaluationDraftCardProps) {
  const { t } = useTranslation();
  const updateEvaluation = useUpdateOntologyEvaluation(matchId);
  const publishEvaluation = usePublishOntologyEvaluation(matchId);
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState(() => currentText(evaluation));

  const isDraft = evaluation.status === 'DRAFT';
  const wasEdited = Boolean(evaluation.editedAt);

  const beginEditing = () => {
    // Re-seed from the server copy: another moderator may have revised it since
    // this card first rendered.
    setDraft(currentText(evaluation));
    setIsEditing(true);
  };

  const save = async () => {
    await updateEvaluation.mutateAsync({
      evaluationId: evaluation.id,
      changes: draft,
    });
    setIsEditing(false);
  };

  const publish = async () => {
    if (await confirmDialog({ message: t('evaluation.ontology.publishConfirm') })) {
      publishEvaluation.mutate(evaluation.id);
    }
  };

  return (
    <article>
      <header>
        <strong>
          {t('evaluation.ontology.evaluationHeading', {
            label:
              evaluation.participantId || t('evaluation.ontology.teamEvaluation'),
            version: evaluation.version,
          })}
        </strong>
        <span
          className={`${evalStyles.ontologyEvaluationStatus} ${STATUS_TONE[evaluation.status] ?? ''}`}
        >
          {ontologyEnumLabel(t, 'evaluationStatus', evaluation.status)}
        </span>
      </header>

      {wasEdited && (
        <p className={evalStyles.ontologyEvaluationEdited}>
          {t('evaluation.ontology.editedNotice', {
            editor: evaluation.editedBy ?? '',
            timestamp: evaluation.editedAt
              ? formatDateTime(evaluation.editedAt)
              : '',
          })}
        </p>
      )}

      {isEditing ? (
        <div className={evalStyles.ontologyEvaluationEdit}>
          <FormField label={t('evaluation.ontology.processHeading')}>
            {(field) => (
              <Textarea
                {...field}
                value={draft.processAssessment}
                rows={6}
                onChange={(event) => setDraft({ ...draft, processAssessment: event.target.value })}
              />
            )}
          </FormField>

          <FormField label={t('evaluation.ontology.outcomeHeading')}>
            {(field) => (
              <Textarea
                {...field}
                value={draft.outcomeAssessment}
                rows={6}
                onChange={(event) => setDraft({ ...draft, outcomeAssessment: event.target.value })}
              />
            )}
          </FormField>

          <FormField label={t('evaluation.ontology.narrativeHeading')}>
            {(field) => (
              <Textarea
                {...field}
                value={draft.narrative}
                rows={10}
                onChange={(event) => setDraft({ ...draft, narrative: event.target.value })}
              />
            )}
          </FormField>

          {updateEvaluation.isError && (
            <p className={evalStyles.ontologyEvaluationError} role="alert">
              {t('evaluation.ontology.saveFailed')}
            </p>
          )}

          <div className={evalStyles.ontologyEvaluationActions}>
            <Button type="button" disabled={updateEvaluation.isPending} onClick={save}>
              {updateEvaluation.isPending
                ? t('evaluation.ontology.saving')
                : t('evaluation.ontology.saveChanges')}
            </Button>
            <Button type="button" variant="ghost" disabled={updateEvaluation.isPending} onClick={() => setIsEditing(false)}
            >
              {t('evaluation.ontology.cancelEdit')}
            </Button>
          </div>
        </div>
      ) : (
        <>
          <h4>{t('evaluation.ontology.processHeading')}</h4>
          <p>
            {evaluation.processAssessment.summary ||
              t('evaluation.ontology.noProcessAssessment')}
          </p>
          <h4>{t('evaluation.ontology.outcomeHeading')}</h4>
          <p>
            {evaluation.outcomeAssessment.summary ||
              t('evaluation.ontology.noOutcomeAssessment')}
          </p>
          <h4>{t('evaluation.ontology.narrativeHeading')}</h4>
          <p>{evaluation.narrative}</p>

          {wasEdited && evaluation.originalContent && (
            <details className={evalStyles.ontologyEvaluationOriginal}>
              <summary>{t('evaluation.ontology.showOriginal')}</summary>
              <h4>{t('evaluation.ontology.processHeading')}</h4>
              <p>
                {evaluation.originalContent.processAssessment?.summary || '—'}
              </p>
              <h4>{t('evaluation.ontology.outcomeHeading')}</h4>
              <p>
                {evaluation.originalContent.outcomeAssessment?.summary || '—'}
              </p>
              <h4>{t('evaluation.ontology.narrativeHeading')}</h4>
              <p>{evaluation.originalContent.narrative || '—'}</p>
            </details>
          )}

          <div className={evalStyles.ontologyEvaluationEvidence}>
            {evaluation.evidenceRefs.map((reference) => {
              const label = t('evaluation.ontology.evidenceRef', {
                type: ontologyEnumLabel(t, 'evidenceType', reference.type),
                id: reference.id,
              });
              return reference.type === 'ACTION' ? (
                <button
                  key={reference.id}
                  type="button"
                  onClick={() => onSelectAction(reference.id)}
                >
                  {label}
                </button>
              ) : (
                <span key={reference.id}>{label}</span>
              );
            })}
          </div>

          {isDraft && (
            <div className={evalStyles.ontologyEvaluationActions}>
              <Button type="button" variant="ghost" onClick={beginEditing}>
                {t('evaluation.ontology.editDraft')}
              </Button>
              <Button type="button" disabled={publishEvaluation.isPending} onClick={publish}>
                {t('evaluation.ontology.publish')}
              </Button>
            </div>
          )}
        </>
      )}
    </article>
  );
}

function currentText(evaluation: OntologyEvaluation) {
  return {
    processAssessment: evaluation.processAssessment.summary ?? '',
    outcomeAssessment: evaluation.outcomeAssessment.summary ?? '',
    narrative: evaluation.narrative,
  };
}
